import { useState, useEffect } from "react"
import { Box, Card, Typography } from "@mui/material"
import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { buildCeoLetterFragment } from "@crm-src/lib/email-fragment-builders"

interface CeoLetterEmailBuilderProps {
  initialContent?: string
  onContentChange: (html: string) => void
}

// CEO Letter — a deliberately bare, personal email. The builder emits ONLY the
// message body fragment (see email-fragment-builders.ts). crm-api's mailer
// (campaign_email.html.erb, `ceo_letter` branch) renders it as a PLAIN email:
// NO logo, NO subject header, NO footer — just a personalized "Hi <first name>,"
// greeting pulled from the recipient contact, the body, and ONE small
// unsubscribe line under it (every marketing email carries one, 2026-10-02).
// (Plus an invisible 1x1 open-tracking pixel.) Because
// crm-api adds the greeting, the body must NOT start with its own. Inline images
// (base64) are CID-embedded at send.
export default function CeoLetterEmailBuilder({
  initialContent,
  onContentChange
}: CeoLetterEmailBuilderProps) {
  const [content, setContent] = useState<string>(
    initialContent || '<p>I wanted to personally reach out and share something I think you will find valuable.</p><p>Write your message here...</p>'
  )

  useEffect(() => {
    onContentChange(buildCeoLetterFragment(content))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content])

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Message body */}
      <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Letter</Typography>
          <InlineAiGenerator
            fieldType="long"
            context="short personal letter from a company executive to a business contact"
            placeholder="e.g., a warm intro, a personal product recommendation"
            currentValue={content}
            onGenerate={setContent}
          />
        </Box>
        <TipTapEditor
          value={content}
          onChange={setContent}
          placeholder="Write your letter here..."
          minHeight={300}
        />
      </Card>
      {/* No inline preview — the always-visible right-hand "Live Preview" pane
          renders this email (with the "Hi <name>," greeting) for every layout. */}
    </Box>
  )
}
